import { forwardRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import Button, { type ButtonProps } from '@mui/material/Button'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import PictureAsPdf from '@mui/icons-material/PictureAsPdf'
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown'
import {
    BOARDGUARD_PDF_LANG_OPTIONS,
    BoardguardPdfLang,
    downloadTicketPdf,
} from '../../utils/boardguard'

type Props = {
    orderId: number
    reservationCode?: string | null
    disabled?: boolean
    /** Full-width labeled button (detail card) or compact icon (list/notifications). */
    variant?: 'button' | 'icon'
    fullWidth?: boolean
    label?: string
    tooltip?: string
    onDownloaded?: (lang: BoardguardPdfLang) => void
    children?: ReactNode
} & Omit<ButtonProps, 'variant' | 'children' | 'onClick'>

export const BoardguardPdfDownload = forwardRef<HTMLButtonElement, Props>(function BoardguardPdfDownload({
    orderId,
    reservationCode,
    disabled = false,
    variant = 'button',
    fullWidth = false,
    label = 'Baixar bilhete (PDF)',
    tooltip = 'Baixar bilhete (PDF)',
    onDownloaded,
    ...buttonProps
}, ref) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)

    const handleSelect = (lang: BoardguardPdfLang) => {
        setAnchorEl(null)
        void downloadTicketPdf(orderId, reservationCode, lang).then(() => onDownloaded?.(lang))
    }

    const menu = (
        <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
            {BOARDGUARD_PDF_LANG_OPTIONS.map((option) => (
                <MenuItem key={option.value} onClick={() => handleSelect(option.value)}>
                    {option.label}
                </MenuItem>
            ))}
        </Menu>
    )

    if (variant === 'icon') {
        // Single Button root so MUI ButtonGroup can cloneElement (size/borders).
        // Menu is portaled so it is not nested in the <button> DOM.
        return (
            <Button
                ref={ref}
                size="small"
                variant="outlined"
                disabled={disabled}
                title={tooltip}
                aria-label={tooltip}
                onClick={(event) => {
                    event.stopPropagation()
                    setAnchorEl(event.currentTarget)
                }}
                {...buttonProps}
            >
                <PictureAsPdf fontSize="small" />
                {typeof document !== 'undefined' ? createPortal(menu, document.body) : null}
            </Button>
        )
    }

    const button = (
        <Button
            ref={ref}
            variant="contained"
            fullWidth={fullWidth}
            disabled={disabled}
            endIcon={<KeyboardArrowDown />}
            onClick={(event) => setAnchorEl(event.currentTarget)}
            {...buttonProps}
        >
            {label}
        </Button>
    )

    return (
        <>
            {fullWidth ? <span style={{ flex: 1, display: 'flex' }}>{button}</span> : button}
            {menu}
        </>
    )
})
